// src/components/admin/ManageAnnouncements.js
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { apiClient } from '../../api/apiClient'; // 确保你有一个配置好的axios实例
import { VersionTag } from '../../components/ui';
import {
    Panel, PanelHeader, PanelTitle, StatusText, AdminButton,
    ModalOverlay, ModalPanel, ModalTitle, ModalFooter, Form, FormGroup, Label, TextInput, TextArea,
} from './adminStyles';
import { color, font } from '../../theme/tokens';

// Styled Components
const Wrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const CardList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 70vh;
    overflow-y: auto;
    padding-right: 4px;
`;

const Card = styled.article`
    padding: 16px 18px;
    background: rgba(7, 2, 5, 0.5);
    border: 1px solid ${color.line};
    transition: border-color 0.2s ease;
    &:hover { border-color: ${color.line2}; }
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
`;

const CardTitle = styled.h4`
    margin: 0;
    font-family: ${font.display};
    font-weight: 700;
    font-size: 16px;
    letter-spacing: 0.08em;
    color: ${color.text};
`;

const CardContent = styled.p`
    margin: 0 0 14px;
    font-size: 14px;
    line-height: 1.8;
    color: ${color.text2};
    white-space: pre-wrap;
`;

const CardFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

const EmptyNote = styled.p`
    margin: 0;
    padding: 16px 0;
    font-size: 13px;
    letter-spacing: 0.16em;
    color: ${color.text3};
`;

// Helper function to format ISO date string for datetime-local input
const formatDateForInput = (isoString) => {
    if (!isoString) return '';
    // new Date(isoString).toISOString() is used to normalize the timezone to UTC
    // Then we slice to get YYYY-MM-DDTHH:MM format
    try {
        const date = new Date(isoString);
        // Offset the date by the timezone difference to display local time correctly
        const timezoneOffset = date.getTimezoneOffset() * 60000;
        const localDate = new Date(date.getTime() - timezoneOffset);
        return localDate.toISOString().slice(0, 16);
    } catch (e) {
        return '';
    }
};
 

// Component
const ManageAnnouncements = () => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null); // For editing

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await apiClient.get('/admin/announcements');
            setItems(response.data);
        } catch (error) {
            console.error("Failed to fetch data:", error);
            // Handle error display
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleCreate = (type) => {
        setCurrentItem({ type, title: '', content: '', version: '', created_at: '' }); // 初始化 created_at
        setIsModalOpen(true);
    };

    const handleEdit = (item) => {
        setCurrentItem({ ...item, created_at: formatDateForInput(item.created_at) });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('确定要删除这项吗？')) {
            try {
                await apiClient.delete(`/admin/announcements/${id}`);
                fetchData(); // Refresh data
            } catch (error) {
                console.error('Failed to delete item:', error);
                alert('删除失败');
            }
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const { id, type, title, content, version, created_at } = currentItem;
        const payload = { type, title, content, version };
        if (created_at) {
            payload.created_at = created_at;
        }

        try {
            if (id) {
                // Update
                await apiClient.put(`/admin/announcements/${id}`, payload);
            } else {
                // Create
                await apiClient.post('/admin/announcements', payload);
            }
            setIsModalOpen(false);
            setCurrentItem(null);
            fetchData();
        } catch (error) {
            console.error('Failed to save item:', error);
            alert('保存失败: ' + (error.response?.data?.message || error.message));
        }
    };
    
    const announcements = items.filter(item => item.type === 'announcement');
    const updateLogs = items.filter(item => item.type === 'update_log');

    return (
        <>
            <Wrapper>
                <Panel>
                    <PanelHeader>
                        <PanelTitle>公告管理</PanelTitle>
                        <AdminButton type="button" $variant="primary" onClick={() => handleCreate('announcement')}>新建公告</AdminButton>
                    </PanelHeader>
                    <CardList>
                        {isLoading && <StatusText>载入中…</StatusText>}
                        {!isLoading && announcements.length === 0 && <EmptyNote>暂无公告。</EmptyNote>}
                        {announcements.map(item => (
                            <Card key={item.id}>
                                <CardHeader>
                                    <CardTitle>{item.title}</CardTitle>
                                </CardHeader>
                                <CardContent>{item.content}</CardContent>
                                <CardFooter>
                                    <AdminButton type="button" $small onClick={() => handleEdit(item)}>编辑</AdminButton>
                                    <AdminButton type="button" $small $variant="danger" onClick={() => handleDelete(item.id)}>删除</AdminButton>
                                </CardFooter>
                            </Card>
                        ))}
                    </CardList>
                </Panel>
                <Panel>
                    <PanelHeader>
                        <PanelTitle>更新日志</PanelTitle>
                        <AdminButton type="button" $variant="primary" onClick={() => handleCreate('update_log')}>新建日志</AdminButton>
                    </PanelHeader>
                    <CardList>
                        {isLoading && <StatusText>载入中…</StatusText>}
                        {!isLoading && updateLogs.length === 0 && <EmptyNote>暂无更新日志。</EmptyNote>}
                        {updateLogs.map(item => (
                            <Card key={item.id}>
                                <CardHeader>
                                    <CardTitle>{item.title}</CardTitle>
                                    <VersionTag>v{item.version}</VersionTag>
                                </CardHeader>
                                <CardContent>{item.content}</CardContent>
                                <CardFooter>
                                    <AdminButton type="button" $small onClick={() => handleEdit(item)}>编辑</AdminButton>
                                    <AdminButton type="button" $small $variant="danger" onClick={() => handleDelete(item.id)}>删除</AdminButton>
                                </CardFooter>
                            </Card>
                        ))}
                    </CardList>
                </Panel>
            </Wrapper>

            {isModalOpen && (
                <ModalOverlay>
                    <ModalPanel $wide role="dialog" aria-modal="true" aria-labelledby="announcement-modal-title">
                        <ModalTitle id="announcement-modal-title">{currentItem?.id ? '编辑' : '创建'}{currentItem?.type === 'announcement' ? '公告' : '更新日志'}</ModalTitle>
                        <Form onSubmit={handleFormSubmit}>
                            <FormGroup>
                                <Label htmlFor="announcement-title">标题</Label>
                                <TextInput
                                    id="announcement-title"
                                    type="text"
                                    placeholder="标题"
                                    required
                                    value={currentItem.title}
                                    onChange={(e) => setCurrentItem({...currentItem, title: e.target.value})}
                                />
                            </FormGroup>
                            {currentItem.type === 'update_log' && (
                                <FormGroup>
                                    <Label htmlFor="announcement-version">版本号</Label>
                                    <TextInput
                                        id="announcement-version"
                                        type="text"
                                        placeholder="版本号 (例如: 1.2.3)"
                                        required
                                        value={currentItem.version}
                                        onChange={(e) => setCurrentItem({...currentItem, version: e.target.value})}
                                        style={{ fontFamily: font.mono }}
                                    />
                                </FormGroup>
                            )}
                            <FormGroup>
                                <Label htmlFor="announcement-content">内容</Label>
                                <TextArea
                                    id="announcement-content"
                                    placeholder="内容 (支持换行)"
                                    required
                                    value={currentItem.content}
                                    onChange={(e) => setCurrentItem({...currentItem, content: e.target.value})}
                                    style={{ minHeight: 150 }}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="created_at_input">创建时间</Label>
                                <TextInput
                                    id="created_at_input"
                                    type="datetime-local"
                                    value={currentItem.created_at || ''}
                                    onChange={(e) => setCurrentItem({...currentItem, created_at: e.target.value})}
                                />
                            </FormGroup>
                            <ModalFooter>
                                <AdminButton type="button" onClick={() => setIsModalOpen(false)}>取消</AdminButton>
                                <AdminButton type="submit" $variant="primary">保存</AdminButton>
                            </ModalFooter>
                        </Form>
                    </ModalPanel>
                </ModalOverlay>
            )}
        </>
    );
};

export default ManageAnnouncements;
